import { Capacitor } from "@capacitor/core";
import { Box, ButtonBase, TablePagination, Typography } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useRecoilValue } from "recoil";

import { Document } from "@/api/generated";
import { PROPERTY_TYPE_VALUES } from "@/api/mappings/property";
import { BottomSheet } from "@/components/BottomSheet";
import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { Loading } from "@/components/Loading";
import { downloadDocumentBlob, navigateNativeDocument } from "@/components/property_documents/documentFiles";
import {
  DocumentLayout,
  DocumentLoadError,
  DocumentMissingProperty,
  useDocumentStyles,
} from "@/components/property_documents/DocumentLayout";
import {
  documentLabel,
  documentMeta,
  MEMO_MAX_LENGTH,
  memoLength,
  nativeDownloadUrl,
} from "@/components/property_documents/documentMetadata";
import { usePropertyDocuments } from "@/components/property_documents/usePropertyDocuments";
import { NavigationPath } from "@/utilities/enums";
import { fileToBase64EncodedImage } from "@/utilities/fileToBase64EncodedImage";
import { SelectedProperty } from "@/view_models/propertySelectors";

type Mode = "add" | "actions" | "delete" | "memo";
type Picker = "camera" | "library" | "file";
export const ManagementPropertyDocumentListPage: React.FC = () => {
  const classes = useDocumentStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const type = Number(id);
  const validType = Number.isInteger(type) && type >= 0 && type <= 11;
  const property = useRecoilValue(SelectedProperty);
  const { documents, loading, error, refresh, updateDocument, removeDocument, api, scope } = usePropertyDocuments(
    property?.id,
    type
  );
  const [modeState, setModeState] = useState<{ scope: string; kind: Mode }>(null);
  const [selection, setSelection] = useState<{ scope: string; document: Document }>(null);
  const [memo, setMemo] = useState("");
  const [operationError, setOperationError] = useState("");
  const [busyScope, setBusyScope] = useState<string>(null);
  const busyRef = useRef(new Set<string>());
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const inputs = { camera, library, file };
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const mode = modeState?.scope === scope ? modeState.kind : null;
  const selected = selection?.scope === scope ? selection.document : null;
  const busy = busyScope === scope;
  const memoMode = mode === "memo" && selected;
  const setMode = (kind: Mode | null) => setModeState(kind ? { scope, kind } : null);
  const consideration = property?.property?.property_type === PROPERTY_TYPE_VALUES.consideration;
  useEffect(
    () => () => {
      activeScope.current = null;
    },
    []
  );
  useEffect(() => {
    setModeState(null);
    setSelection(null);
    setMemo("");
    setOperationError("");
    setBusyScope(null);
    setPage(0);
  }, [scope]);
  useEffect(() => {
    if (!validType) history.goBack();
  }, [history, validType]);
  useEffect(() => {
    if (mode !== "delete") return;
    document.body.setAttribute("data-property-document-confirm", "");
    return () => {
      document.body.removeAttribute("data-property-document-confirm");
    };
  }, [mode]);
  useEffect(() => {
    setPage((previous) => Math.min(previous, Math.max(0, Math.ceil(documents.length / rowsPerPage) - 1)));
  }, [documents.length, rowsPerPage]);

  const close = () => {
    if (busyRef.current.has(scope)) return;
    setMode(null);
    setSelection(null);
    setOperationError("");
  };
  const runOperation = async (failure: string, operation: () => Promise<void>) => {
    if (busyRef.current.has(scope) || activeScope.current !== scope) return;
    busyRef.current.add(scope);
    setBusyScope(scope);
    setOperationError("");
    try {
      await operation();
    } catch (e) {
      console.error(e);
      if (activeScope.current === scope) setOperationError(failure);
    } finally {
      busyRef.current.delete(scope);
      if (activeScope.current === scope) setBusyScope(null);
    }
  };
  const handleFileInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const uploaded = input.files?.[0];
    if (!uploaded) return;
    await runOperation("書類の追加に失敗しました。もう一度お試しください。", async () => {
      try {
        const encoded = await fileToBase64EncodedImage(uploaded);
        if (activeScope.current !== scope) return;
        await api.v1PropertiesPropertyIdDocumentsDocumentTypeFilesPost(property.id, type, encoded, {
          withCredentials: true,
        });
        if (activeScope.current === scope) {
          setMode(null);
          await refresh();
        }
      } finally {
        input.value = "";
      }
    });
  };
  const download = async () => {
    if (!selected) return;
    const target = selected;
    await runOperation("ダウンロードに失敗しました。もう一度お試しください。", async () => {
      if (Capacitor.isNativePlatform()) {
        const { data } = await api.v1PropertiesPropertyIdDocumentsDocumentTypeFilesFileIdUrlGet(
          property.id,
          type,
          target.id,
          target.name,
          { withCredentials: true }
        );
        const url = nativeDownloadUrl(data);
        if (!url) throw new Error("Download URL is missing");
        if (activeScope.current === scope) navigateNativeDocument(url);
      } else {
        const { data } = await api.v1PropertiesPropertyIdDocumentsDocumentTypeFilesFileIdGet(
          property.id,
          type,
          target.id,
          { responseType: "blob", withCredentials: true }
        );
        if (activeScope.current === scope) downloadDocumentBlob(data as unknown as Blob, target.name);
      }
      if (activeScope.current === scope) closeAfterOperation();
    });
  };
  const closeAfterOperation = () => {
    setMode(null);
    setSelection(null);
  };
  const deleteDocument = async () => {
    if (!selected) return;
    const target = selected;
    await runOperation("削除に失敗しました。もう一度お試しください。", async () => {
      await api.v1PropertiesPropertyIdDocumentsDocumentTypeFilesFileIdDelete(property.id, type, target.id, {
        withCredentials: true,
      });
      if (activeScope.current === scope) {
        removeDocument(target.id);
        closeAfterOperation();
        await refresh();
      }
    });
  };
  const saveMemo = async () => {
    if (!selected || memoLength(memo) > MEMO_MAX_LENGTH) return;
    const target = selected,
      value = memo;
    await runOperation("メモの保存に失敗しました。もう一度お試しください。", async () => {
      const { data } = await api.v1PropertiesPropertyIdDocumentsDocumentTypeFilesFileIdPatch(
        property.id,
        type,
        target.id,
        { memo: value },
        { withCredentials: true }
      );
      if (activeScope.current === scope) {
        updateDocument({ ...target, ...data, memo: data.memo ?? value });
        closeAfterOperation();
      }
    });
  };
  const selectDocument = (document: Document) => {
    if (busyRef.current.has(scope)) return;
    setSelection({ scope, document });
    setOperationError("");
    setMode("actions");
  };
  const pick = (picker: Picker) => {
    if (!busyRef.current.has(scope)) inputs[picker].current?.click();
  };
  const onBack = () => {
    if (busyRef.current.has(scope)) return;
    if (memoMode) close();
    else history.push("/property_document_list");
  };
  const count = memoLength(memo);
  if (!validType) return null;
  if (!property)
    return <DocumentMissingProperty title={documentLabel(type)} onSelect={() => history.push("/management/own")} />;
  return (
    <DocumentLayout
      title={memoMode ? "メモ" : documentLabel(type)}
      backLabel={memoMode ? documentLabel(type) : "書類"}
      onBack={onBack}
      navigationPath={
        consideration ? NavigationPath.PROPERTY_MANAGEMENT_CONSIDERATION : NavigationPath.PROPERTY_MANAGEMENT_OWNED
      }
      memo={Boolean(memoMode)}
    >
      {memoMode ? (
        <Box className={`${classes.body} ${classes.memo}`}>
          <Box>
            <Typography component="h2" className={classes.heading}>
              メモ
            </Typography>
            <Typography className={classes.small}>更新の時期や、提出先を控えておけます。</Typography>
          </Box>
          <Box className={classes.target}>
            <Typography className={classes.rowTitle}>{selected.name}</Typography>
            <Typography className={classes.small}>{documentMeta(selected, true)}</Typography>
          </Box>
          <Box>
            <textarea
              aria-label="書類のメモ"
              aria-describedby="document-memo-counter"
              aria-invalid={count > MEMO_MAX_LENGTH}
              className={classes.textarea}
              value={memo}
              disabled={busy}
              onChange={(event) => setMemo(event.target.value)}
              placeholder="メモを入力"
            />
            <Typography id="document-memo-counter" className={count > MEMO_MAX_LENGTH ? classes.error : classes.small}>
              {count} / {MEMO_MAX_LENGTH}文字
            </Typography>
            {count > MEMO_MAX_LENGTH && (
              <Typography role="alert" className={classes.error}>
                メモは30文字以内で入力してください。
              </Typography>
            )}
          </Box>
          {operationError && (
            <Typography role="alert" className={classes.error}>
              {operationError}
            </Typography>
          )}
          <CustomButton
            buttonSize="L"
            fullWidth
            className={classes.actionButton}
            disabled={busy || count > MEMO_MAX_LENGTH}
            onClick={() => {
              void saveMemo();
            }}
          >
            {busy ? "保存中…" : "保存する"}
          </CustomButton>
        </Box>
      ) : (
        <Box className={classes.body}>
          {loading && documents.length === 0 ? (
            <Loading />
          ) : (
            <>
              {error && (
                <DocumentLoadError
                  onRetry={() => {
                    void refresh();
                  }}
                />
              )}
              {!error && documents.length === 0 && (
                <Box className={classes.empty}>
                  <Typography component="h2" className={classes.sectionTitle}>
                    書類はまだありません
                  </Typography>
                  <Typography className={classes.small}>
                    {type === 8
                      ? "火災保険・地震保険の証券を、写真でもPDFでも保管できます。事故のときや確定申告のときにすぐ取り出せます。"
                      : `${documentLabel(type)}を、写真でもPDFでも保管できます。必要なときにすぐ確認できます。`}
                  </Typography>
                </Box>
              )}
              {documents.length > 0 && (
                <Box className={classes.section}>
                  <Typography component="h2" className={classes.sectionTitle}>
                    保管している書類（{documents.length}件）
                  </Typography>
                  <Box className={classes.rows}>
                    {documents.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((document) => (
                      <ButtonBase
                        key={document.id}
                        className={classes.row}
                        onClick={() => selectDocument(document)}
                        disabled={busy}
                      >
                        <Box className={classes.fileBody}>
                          <Typography className={classes.rowTitle}>{document.name}</Typography>
                          <Typography className={classes.small}>{documentMeta(document)}</Typography>
                          {document.memo && <Typography className={classes.small}>メモ：{document.memo}</Typography>}
                        </Box>
                        <Chevron />
                      </ButtonBase>
                    ))}
                  </Box>
                  <TablePagination
                    component="div"
                    className={classes.pagination}
                    count={documents.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 100]}
                    onPageChange={(_, next) => setPage(next)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(Number(event.target.value));
                      setPage(0);
                    }}
                    labelRowsPerPage="表示件数"
                    labelDisplayedRows={({ from, to, count: total }) => `${from}–${to} / ${total}件`}
                    nextIconButtonProps={{ "aria-label": "次のページ" }}
                    backIconButtonProps={{ "aria-label": "前のページ" }}
                  />
                </Box>
              )}
            </>
          )}
          {operationError && !mode && (
            <Typography role="alert" className={classes.error}>
              {operationError}
            </Typography>
          )}
          <CustomButton
            buttonSize="L"
            fullWidth
            className={classes.actionButton}
            disabled={busy}
            onClick={() => {
              setOperationError("");
              setMode("add");
            }}
          >
            書類を追加
          </CustomButton>
        </Box>
      )}
      <input
        ref={camera}
        hidden
        type="file"
        accept="image/*"
        capture="environment"
        aria-label="写真を撮る"
        onChange={handleFileInput}
      />
      <input
        ref={library}
        hidden
        type="file"
        accept="image/*"
        aria-label="ライブラリから選ぶ"
        onChange={handleFileInput}
      />
      <input
        ref={file}
        hidden
        type="file"
        accept="image/*,application/pdf"
        aria-label="ファイルから選ぶ"
        onChange={handleFileInput}
      />
      <BottomSheet
        titleBreakWord
        open={mode === "add" || (mode === "actions" && Boolean(selected))}
        title={mode === "add" ? "書類を追加" : (selected?.name ?? "")}
        description={mode === "add" ? "写真でもPDFでも保管できます" : selected && documentMeta(selected, true)}
        onOpen={() => {}}
        onClose={close}
        contentPadding={0}
      >
        {operationError && (
          <Box px={3} py={1}>
            <Typography role="alert" className={classes.error}>
              {operationError}
            </Typography>
          </Box>
        )}
        {mode === "add" ? (
          <>
            {(
              [
                ["camera", "写真を撮る"],
                ["library", "ライブラリから選ぶ"],
                ["file", "ファイルから選ぶ"],
              ] as [Picker, string][]
            ).map(([picker, label]) => (
              <ButtonBase key={picker} className={classes.menuRow} disabled={busy} onClick={() => pick(picker)}>
                {label}
              </ButtonBase>
            ))}
            {busy && <Typography className={classes.small}>追加中…</Typography>}
          </>
        ) : (
          <>
            <ButtonBase
              component="a"
              href={!busy ? (selected?.file_url ?? undefined) : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.menuRow}
              disabled={busy || !selected?.file_url}
              onClick={close}
            >
              開く
            </ButtonBase>
            <ButtonBase
              className={classes.menuRow}
              disabled={busy}
              onClick={() => {
                void download();
              }}
            >
              {busy ? "ダウンロード中…" : "ダウンロード"}
            </ButtonBase>
            <ButtonBase
              className={classes.menuRow}
              disabled={busy}
              onClick={() => {
                setMemo(selected?.memo ?? "");
                setOperationError("");
                setMode("memo");
              }}
            >
              メモ
            </ButtonBase>
            <ButtonBase
              className={`${classes.menuRow} ${classes.destructive}`}
              disabled={busy}
              onClick={() => {
                setOperationError("");
                setMode("delete");
              }}
            >
              削除
            </ButtonBase>
          </>
        )}
        <ButtonBase className={`${classes.menuRow} ${classes.cancel}`} disabled={busy} onClick={close}>
          キャンセル
        </ButtonBase>
      </BottomSheet>
      {mode === "delete" && selected && (
        <ConfirmationDialog
          title="書類を削除しますか？"
          description={
            <>
              <span style={{ overflowWrap: "anywhere" }}>
                「{selected.name}」を削除します。
                <br />
                削除した書類は元に戻せません。
              </span>
              {operationError && (
                <Typography role="alert" className={classes.error}>
                  {operationError}
                </Typography>
              )}
            </>
          }
          confirmLabel={busy ? "削除中…" : "削除する"}
          cancelLabel="キャンセル"
          destructive
          confirmDisabled={busy}
          cancelDisabled={busy}
          onConfirm={() => {
            void deleteDocument();
          }}
          onCancel={close}
          onClickOutside={close}
        />
      )}
    </DocumentLayout>
  );
};
